<template>
  <div class="hist">
    <div class="hist-toolbar">
      <el-checkbox-group v-model="visibleSeries" size="small" @change="render">
        <el-checkbox v-for="s in SERIES" :key="s.key" :value="s.key">
          <i class="sw" :class="s.key === 'sp' ? 'dash' : ''" :style="{ borderColor: s.color() }" />
          {{ t(`message.rtu.series.${s.key}`) }}
        </el-checkbox>
      </el-checkbox-group>
      <span class="spacer" />
      <el-radio-group v-model="rangeHours" size="small" @change="load">
        <el-radio-button :value="24">{{ t('message.rtu.range24h') }}</el-radio-button>
        <el-radio-button :value="168">{{ t('message.rtu.range7d') }}</el-radio-button>
        <el-radio-button :value="720">{{ t('message.rtu.range30d') }}</el-radio-button>
      </el-radio-group>
      <el-button size="small" :disabled="!lastH" @click="exportCsv">
        {{ t('message.rtu.exportCsv') }}
      </el-button>
    </div>
    <div ref="tempEl" class="chart-temp" v-loading="loading" />
    <div class="lanes-toolbar">
      <span class="lanes-h">{{ t('message.rtu.runStates') }}</span>
      <el-checkbox-group v-model="visibleLanes" size="small" @change="render">
        <el-checkbox v-for="k in LANE_KEYS" :key="k" :value="k">{{ t(`message.rtu.lanes.${k}`) }}</el-checkbox>
      </el-checkbox-group>
    </div>
    <div ref="lanesEl" class="chart-lanes" :style="{ height: lanesHeight + 'px' }" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, defineProps } from 'vue';
import { useI18n } from 'vue-i18n';
import * as echarts from 'echarts';

const props = defineProps({
  adapter: { type: Object, required: true },
  unitId: { type: String, required: true },
});
const { t } = useI18n();

const tempEl = ref(null);
const lanesEl = ref(null);
const rangeHours = ref(24);
const loading = ref(false);
let tempChart = null;
let lanesChart = null;
let lastH = null; // 最近一次加载的历史数据（导出/重绘用）

// 已做色盲校验的四系列配色（浅/深各自通过 dataviz 校验）
const isDark = () =>
  document.documentElement.getAttribute('data-theme') === 'dark' ||
  (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches &&
   document.documentElement.getAttribute('data-theme') !== 'light');
const C_ROOM = () => (isDark() ? '#2E8FF2' : '#409EFF');
const C_SP = () => '#B8860B';
const C_OAT = () => '#2F9E77';
const C_SAT = () => '#9A60B4';
const C_ALARM = '#F56C6C';

const SERIES = [
  { key: 'sp', field: 'sp', color: C_SP, dashed: true },
  { key: 'room', field: 'temp', color: C_ROOM },
  { key: 'oat', field: 'oat', color: C_OAT },
  { key: 'sat', field: 'sat', color: C_SAT },
];
const LANE_KEYS = ['fan', 'compressor', 'heat', 'occupied'];

const visibleSeries = ref(['sp', 'room']);
const visibleLanes = ref([...LANE_KEYS]);
const lanesHeight = computed(() => 40 + visibleLanes.value.length * 28);

function tempOption(h) {
  const active = SERIES.filter((s) => visibleSeries.value.includes(s.key));
  const allVals = [];
  active.forEach((s) => h.points.forEach((p) => { const v = p[s.field]; if (v != null) allVals.push(v); }));
  const min = allVals.length ? Math.floor(Math.min(...allVals) / 2) * 2 - 2 : 58;
  const max = allVals.length ? Math.ceil(Math.max(...allVals) / 2) * 2 + 2 : 78;

  const series = active.map((s) => ({
    name: t(`message.rtu.series.${s.key}`),
    type: 'line',
    step: s.key === 'sp' ? 'end' : undefined,
    symbol: 'none',
    data: h.points.filter((p) => p[s.field] != null).map((p) => [p.t, p[s.field]]),
    lineStyle: { width: 2, color: s.color(), type: s.dashed ? 'dashed' : 'solid' },
    itemStyle: { color: s.color() },
    endLabel: (s.key === 'room' || s.key === 'sp') ? {
      show: true, fontSize: 11, fontWeight: 600, color: s.color(),
      formatter: (p) => `${t(`message.rtu.series.${s.key}`)} ${Number(p.value[1]).toFixed(1)}°`,
    } : undefined,
  }));

  if (series.length) {
    series[series.length - 1].markLine = {
      symbol: 'none', animation: false,
      label: { formatter: (p) => p.name, position: 'insideEndTop', color: C_ALARM, fontWeight: 600, fontSize: 11 },
      lineStyle: { color: C_ALARM, type: 'dashed' },
      data: h.faults.map((f) => ({
        xAxis: f.t.getTime(),
        name: `${t(`message.rtu.ind.${f.key}`)} ${f.t.getHours()}:${String(f.t.getMinutes()).padStart(2, '0')}`,
      })),
    };
  }

  return {
    animation: false,
    grid: { left: 48, right: 20, top: 28, bottom: 28 },
    tooltip: { trigger: 'axis', valueFormatter: (v) => `${Number(v).toFixed(1)}°F` },
    xAxis: { type: 'time', axisLine: { show: false }, axisTick: { show: false } },
    yAxis: {
      type: 'value', min, max,
      axisLabel: { formatter: '{value}°' },
      splitLine: { lineStyle: { opacity: 0.4 } },
    },
    series,
  };
}

function lanesOption(h) {
  const activeKeys = LANE_KEYS.filter((k) => visibleLanes.value.includes(k));
  const start = h.points.length ? h.points[0].t.getTime() : Date.now() - 3600e3;
  const end = h.points.length ? h.points[h.points.length - 1].t.getTime() : Date.now();
  const data = [];
  h.lanes.forEach((lane) => {
    const idx = activeKeys.indexOf(lane.key);
    if (idx < 0) return;
    lane.bands.forEach(([a, b]) => data.push({ value: [idx, a, b] }));
  });
  return {
    animation: false,
    grid: { left: 90, right: 20, top: 6, bottom: 26 },
    tooltip: {
      formatter: (p) => {
        const [idx, a, b] = p.value;
        const f = (ms) => new Date(ms).toLocaleString();
        return `${t(`message.rtu.lanes.${activeKeys[idx]}`)}<br/>${f(a)} → ${f(b)}`;
      },
    },
    xAxis: { type: 'time', min: start, max: end, axisLine: { show: false }, axisTick: { show: false } },
    yAxis: {
      type: 'category',
      data: activeKeys.map((k) => t(`message.rtu.lanes.${k}`)),
      inverse: true,
      axisLine: { show: false }, axisTick: { show: false },
    },
    series: [{
      type: 'custom',
      renderItem: (params, api) => {
        const idx = api.value(0);
        const a = api.coord([api.value(1), idx]);
        const b = api.coord([api.value(2), idx]);
        const height = 14;
        return {
          type: 'rect',
          shape: { x: a[0], y: a[1] - height / 2, width: Math.max(2, b[0] - a[0]), height, r: 3 },
          style: { fill: C_ROOM(), opacity: 0.55 },
        };
      },
      encode: { x: [1, 2], y: 0 },
      data,
    }],
  };
}

function render() {
  if (!lastH) return;
  tempChart?.setOption(tempOption(lastH), true);
  lanesChart?.setOption(lanesOption(lastH), true);
  // 泳道数量变化 → 高度变化
  setTimeout(() => lanesChart?.resize(), 50);
}

async function load() {
  loading.value = true;
  try {
    lastH = await props.adapter.getHistory(props.unitId, rangeHours.value);
    render();
  } catch (e) {
    console.error('[rtu] history load failed:', e);
  } finally {
    loading.value = false;
  }
}

function exportCsv() {
  if (!lastH) return;
  const laneAt = (laneKey, tMs) => {
    const lane = lastH.lanes.find((l) => l.key === laneKey);
    return lane && lane.bands.some(([a, b]) => tMs >= a && tMs < b) ? 1 : 0;
  };
  const cols = ['time', 'setpoint_F', 'room_temp_F', 'oat_F', 'sat_F',
    'fan_on', 'compressor_on', 'heat_on', 'occupied'];
  const fmtN = (v) => (v == null ? '' : Number(v).toFixed(2));
  const pad = (n) => String(n).padStart(2, '0');
  const stamp = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  const rows = lastH.points.map((p) => [
    stamp(p.t), fmtN(p.sp), fmtN(p.temp), fmtN(p.oat), fmtN(p.sat),
    laneAt('fan', p.t.getTime()), laneAt('compressor', p.t.getTime()),
    laneAt('heat', p.t.getTime()), laneAt('occupied', p.t.getTime()),
  ]);
  // CRLF：Excel（尤其中文版）对 \n 兼容性差；BOM 已由下方 Blob 前缀提供
  let csv = cols.join(',') + '\r\n' + rows.map((r) => r.join(',')).join('\r\n');
  if (lastH.faults.length) {
    csv += '\r\n\r\nfault_time,fault\r\n' +
      lastH.faults.map((f) => `${stamp(f.t)},${f.key}`).join('\r\n');
  }
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `rtu_${props.unitId.replace(/[^\w.-]/g, '_')}_${rangeHours.value}h_trend.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

const onResize = () => { tempChart?.resize(); lanesChart?.resize(); };

onMounted(() => {
  tempChart = echarts.init(tempEl.value);
  lanesChart = echarts.init(lanesEl.value);
  window.addEventListener('resize', onResize);
  load();
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
  tempChart?.dispose();
  lanesChart?.dispose();
});
</script>

<style scoped>
.hist-toolbar { display: flex; align-items: center; gap: 12px; padding-bottom: 6px; flex-wrap: wrap; }
.hist-toolbar .spacer { flex: 1; }
.sw { display: inline-block; width: 14px; height: 0; border-top: 2.5px solid; vertical-align: middle; margin-right: 4px; }
.sw.dash { border-top-style: dashed; }
.chart-temp { width: 100%; height: 260px; }
.lanes-toolbar { display: flex; align-items: center; gap: 14px; margin-top: 4px; flex-wrap: wrap; }
.lanes-h { font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: var(--el-text-color-secondary); }
.chart-lanes { width: 100%; }
</style>
